import { mutationUpdate, type UpdateHookResponse } from '../hooks'
import { useTantalimMutation } from '../mutation'
import { useProfile } from '../user'
import { useUserCache } from '../user/cache'
import { applyCompanyUpdate, useCompanyCache } from './cache'
import {
  type CompanyData,
  type CompanyUpdateInput,
  type CompanyUpdateSuccess,
  Logger,
  type PartialCompanyData,
  toResponseError,
} from '@terros/common'

const logger = new Logger('useCompanies')

export function useUpdateCompany(): UpdateHookResponse<PartialCompanyData, CompanyData> {
  logger.verbose('calling useUpdateCompany')
  const profile = useProfile()
  const cache = useCompanyCache()
  const userCache = useUserCache()

  const mutation = useTantalimMutation<CompanyUpdateInput, CompanyUpdateSuccess>({ url: '/company/update' })

  async function update(company: PartialCompanyData): Promise<CompanyData> {
    const previous = cache.getItem(company.companyId)
    if (previous) cache.updateItem(applyCompanyUpdate(previous, company))

    try {
      logger.verbose('starting update')
      const response = await mutation.mutateAsync({ company })
      if (response.company) {
        cache.updateCompany(response.company)
        if (profile.company?.companyId === company.companyId) {
          userCache.clearProfile()
        }
      }
      logger.verbose('done with update')
      return response.company
    } catch (error) {
      if (previous) cache.updateItem(previous)
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}
